'use client';

import { SoundToggle } from './cinema';
import { Link } from '../link';
import styles from './ui.module.css';
import { Logo } from './brand';
import { ChromeLink } from './chrome-link';
import {
  navSlots,
  SIGNED_OUT,
  topNav,
  type NavItem,
  type NavStanding,
} from '@goodbuild/rules/site';
import type { SiteId } from '@goodbuild/rules/sites';
import { displayName } from '@goodbuild/rules/display-name';
import { useAccount } from '../account';

/**
 * The site header, which is different on each of the three sites.
 *
 * **A client component, and it has to be one.** It reads `useAccount()`, and
 * that context is populated by `AccountProvider`, which is `'use client'`
 * because three applications establish "who is signed in" three different ways
 * — `auth()` in a Next server component, `useAuth()` in the React app,
 * `Astro.locals.auth()` in the storefront. This lived in `ui.tsx` next to
 * `Page`, where it made the whole production build fail at the first
 * prerendered route: *attempted to call useAccount() from the server*. Nothing
 * caught it, because `tsc` cannot see a server/client boundary and the dev
 * server renders those routes per request rather than prerendering them.
 *
 * `Page` stays a server component and renders this. That direction is allowed;
 * the reverse is not.
 *
 * **One bar, on all three sites.** It was three, chosen by hostname, and the
 * hostname is the wrong question: the React app is one deployment serving all
 * three audiences, so `resolveSite` answers `store` for every page in it and a
 * studio standing in its own portal was reading the player's bar. What goes in
 * it now is what this account can actually open — `topNav` in `../site` argues
 * the whole of it, and this component's only job is to draw the result on the
 * right origin.
 *
 * The site switcher went with the three bars. It existed to cross between them;
 * a bar that already holds every place you can go has nowhere to switch to, and
 * `Store` beside `Games` was two links to the same shop.
 *
 * **Two shapes, and which one is drawn is decided by whether anybody is signed
 * in.** Not by two components and not by a prop — by how much there is to put
 * in the bar, which is the only thing that differs.
 *
 * Signed in there is a hierarchy to draw and the bar is two rows: the mark and
 * the furniture above, small; every place this account can go below, at three
 * times the size. That is the bar as it has been, and `slot` is the one thing
 * that moved in it — `Launcher` and `Admin` went up to the small row, because a
 * preference panel and a staff console are not answers to *where can I go* and
 * were being drawn as if they were.
 *
 * Signed out there are four things in the entire header — the mark, `Games`,
 * the installer, and the two doors — and a hierarchy imposed on four things is
 * a hierarchy imposed on nothing. Stacked, the second row held one word and the
 * hairline above it separated a wordmark from the single link beneath it. So
 * they sit on one line at one weight, which is what a header with nothing to
 * organise should look like.
 *
 * The rows are the same components either way — `.navPrimary` holds the
 * destinations and `.navAccount` holds the doors in both shapes — so the
 * current-page marker, the sweep and the account fallback are written once. It
 * is the arrangement that differs, and `.navSingle` is the whole of it.
 */
export interface SiteNavProps {
  site: SiteId;
  /**
   * Whether somebody is signed in.
   *
   * Passed in rather than read, because the two applications establish it
   * differently and neither can do the other's way: the React app asks the API
   * with `useSession()`, and the storefront — a different origin, with no
   * access to the token that would make that call — reads a cookie the app
   * writes for it, resolved in its middleware. The nav only needs the answer.
   */
  signedIn?: boolean | undefined;
  /**
   * The account widget, or nothing at all on a signed-out surface.
   *
   * Optional even when signed in. The storefront has no React node to give —
   * `.astro` markup is not one — so leaving this out falls back to a plain link
   * to the account page, which is the whole of what a store header needs to
   * offer.
   */
  account?: React.ReactNode | undefined;
  /**
   * What to call this visitor, when there is no widget to say it with.
   *
   * **A string, and never a node, and that distinction is the whole of why this
   * prop exists** (#113). The React app builds an `<Account name={…} />` and
   * passes it as `account` above, because it can: it is React, and it knows the
   * name from the session it holds. The storefront cannot build a node at all —
   * `.astro` markup is not one, and passing one is the failure that answered
   * every page on the site with a 200 and an empty body — so it had no way to
   * say the name and fell through to the literal word "Account". The same
   * account, two applications, and a bar that greeted a person on one of them.
   *
   * A string crosses that gap where a node cannot. The store reads it from the
   * `gb_name` cookie in its middleware and hands it down as text; see
   * `@goodbuild/rules/display-name`, which argues why a name gets a cookie of
   * its own rather than a fifth field in the session hint, and why nothing may
   * ever believe what it says.
   *
   * Only ever read when there is no `account` node, because the node already
   * contains the name and drawing both would be saying it twice. Absent, or a
   * name that normalises to nothing, falls back to the word "Account" — which
   * is what this bar said before there was a name to say.
   *
   * `null` is admitted beside `undefined`, and the two mean different things
   * worth keeping apart: `undefined` is *nobody told us*, and `null` is *we
   * looked and there is no name*. The storefront's middleware resolves the
   * second — a crawler, a signed-out visitor, an account created through a
   * provider that gave us none — and it hands the value straight over. A
   * `?? undefined` at that hand-off would be a conversion whose only purpose is
   * to satisfy a type, at the one seam where the type is what holds the two
   * applications' contracts together. Both render "Account" either way.
   */
  accountName?: string | null | undefined;
  /*
   * **`appOrigin` and `storeOrigin` were props here and are gone.**
   *
   * The storefront and the signed-in app were separate Workers on separate
   * hosts, and this bar is shared: `Sign in` was a 404 on the store and `Games`
   * was a 404 on the app, each for the same reason in the opposite direction.
   * Both were fixed by handing the component the other surface's origin.
   *
   * One deployment serves every entry in this bar now, so every href in it is
   * correct relative to whatever host answered. See the note where `chromeHref`
   * used to be, in `../site`.
   */
  /**
   * Which of the three role links this account may have.
   *
   * Passed in rather than read, exactly like `signedIn` and for the same
   * reason: the React app has `/me` and the storefront has a cookie. Omitted,
   * the context answers; omitted there too, nobody has a role and the bar is
   * `Games` and `Library`, which is what a surface that cannot tell should
   * draw rather than guessing.
   *
   * **Both applications pass it now, and until #62 only one did.** The
   * storefront rendered this component without it, took the default, and drew a
   * bar with no role links in it for accounts that had all three — the same
   * account, two different bars, one link apart. The cookie carries the roles
   * since then; what has not changed, and must not, is that this component
   * decides nothing from them beyond which entries `topNav` returns.
   */
  standing?: NavStanding | undefined;
  /**
   * The path being viewed, so the nav can mark it.
   *
   * `.navCurrent` has existed in the stylesheet since the chrome was written
   * and had never been applied to anything: `ChromeLink` accepts a class name
   * and nothing passed one, so "which of these am I looking at" was a style
   * with no caller. Optional, because a surface that does not know its own path
   * should draw no marker rather than guess at one.
   *
   * May carry a query string and a fragment, because the callers hand over
   * whatever the surface knows — `Astro.url.pathname` on the store, the
   * router's location in the app, and a rail entry has carried `?org=…` since
   * #88. `current` below cuts both off before comparing anything.
   */
  currentPath?: string | undefined;
}

export function SiteNav(props: SiteNavProps): React.ReactElement {
  const fromContext = useAccount();
  const { site } = props;
  // Explicit props win; otherwise the application's AccountProvider answers.
  const signedIn = props.signedIn ?? fromContext.signedIn;
  const account = props.account ?? fromContext.account;
  /*
   * What to draw, from who is asking rather than from which host answered.
   *
   * The standing comes from the application, like `signedIn` does and for the
   * same reason: the React app asks `/me`, and the storefront — a different
   * origin with no access to the token — reads the session-hint cookie in its
   * middleware. Both hand the same four booleans to the same `topNav`, which is
   * what makes the bar identical on the two applications for one account rather
   * than merely similar. See `lib/session-hint` for why a cookie may carry
   * those four booleans and may never carry a fifth thing that identifies
   * anybody.
   *
   * Undefined from both is still *nobody told us*, and still reads as no roles:
   * a link too few costs a detour through the account page, and a link too many
   * is a door that opens onto a refusal.
   *
   * **Two differences between the two bars survived #113's audit on purpose,
   * and both look like the bug that was fixed.**
   *
   * — `standing.launcher` is set by the React app and never by the storefront,
   *   so `Downloads` and `Launcher` appear in one bar and not the other for the
   *   same account. That is correct: it is not a fact about the account but
   *   about the program doing the asking, the same person is in the launcher on
   *   one machine and a browser on another at the same moment, and no cookie
   *   could carry it honestly. A web player offered `Downloads` would get two
   *   screens whose every button calls a bridge that is not there. See
   *   `NavStanding.launcher` in `../site`.
   * — The bar is painted on a different ground in a workplace than on the
   *   store. That is brand guidelines section 04 — one channel per surface —
   *   and it is the wayfinding working rather than the chrome disagreeing with
   *   itself. `SiteChrome` resolves it as `space ?? site` and this component
   *   never asks.
   */
  const standing = props.standing ?? fromContext.standing;
  const items = topNav({ ...(standing ?? SIGNED_OUT), signedIn });

  /*
   * The two rows, divided by kind rather than by fit.
   *
   * `navSlots` and not a `filter` here, so the default — an entry that says
   * nothing about its slot is a destination — is decided in the file that
   * argues the bar rather than in the file that draws it. See `NavItem.slot`.
   *
   * `utility` is empty for everybody signed out, which is not a case this
   * component has to handle: the signed-out bar is one row and does not ask.
   */
  const { primary, utility } = navSlots(items);

  /*
   * Which entry names the section this page is in.
   *
   * **This was an exact match, and an exact match marks almost nothing in this
   * bar** (#113). Every role entry here is a section root — `/portal`, `/admin`,
   * `/board` — and between them they cover some thirty routes in the
   * React app, not one of which is the root itself. So a studio anywhere in its
   * own portal saw an unmarked bar, an administrator on any of eleven staff
   * screens saw an unmarked bar, and the storefront's `/browse` was marked
   * because it happens to be a leaf. Two applications, one rule, and it worked
   * on one page of one of them.
   *
   * A section match, on a slash boundary. `/portal/stats` is in `/portal` and
   * `/boardroom` is not in `/board` — a bare `startsWith` would claim it, and
   * that is the failure mode the old comment here was written against.
   *
   * **What makes it safe is that no two entries in this bar share a prefix.**
   * That is a property of `topNav` rather than of this function, so it is
   * asserted rather than assumed: `site-chrome.test.ts` sweeps every standing
   * and fails if an entry is ever added underneath another one. If that day
   * comes, this rule has to become "the longest entry that matches" rather than
   * "any entry that matches", or two things light up at once.
   *
   * **The rail keeps the exact match, and that is not an oversight** — see
   * `WorkNav`, where the note says so from the other side. `/portal` and
   * `/portal/stats` are both entries in the rail, so a section rule there would
   * mark both of them on the statistics screen; the bar holds only one of the
   * two, so the same rule that is wrong down the side is the only right one
   * across the top.
   *
   * The query and the fragment are cut off first. A rail entry has carried
   * `?org=…` since #88, and what a path names is decided by the path.
   */
  const viewing = props.currentPath?.split(/[?#]/, 1)[0];
  const isCurrent = (path: string): boolean =>
    viewing !== undefined && (viewing === path || viewing.startsWith(`${path}/`));

  /*
   * One entry, drawn the same way wherever it lands.
   *
   * Written out three times before — once in the stacked bar, and it would have
   * been three the moment there were two rows and a one-row shape. The marker
   * is two things that have to agree (a class and an ARIA attribute, said in
   * both places for the reason on `ChromeLink.current`), and three copies of a
   * pair that has to agree is two chances to update one of them.
   *
   * `.navCurrent` is unqualified here on purpose: the stylesheet qualifies it
   * per group — `.navPrimary a.navCurrent` and `.navUtility a.navCurrent` —
   * because a bare class loses to a class-and-type selector, and the group is
   * what supplies the size the marker has to match.
   */
  const entry = (item: NavItem): React.ReactElement => {
    /*
      Said twice on purpose: once in the stylesheet and once to a screen reader.
      `.navCurrent` is a colour and a rule, and a colour is not available to
      somebody who is not looking at the page — the rail has carried
      `aria-current="page"` since it was written and the bar carried nothing
      until #113. `ChromeLink` takes the boolean and decides the attribute, on
      both the anchor and the routed branch.
    */
    const here = isCurrent(item.href);

    return (
      <ChromeLink
        key={item.href}
        item={item}
        className={here ? styles.navCurrent : undefined}
        current={here}
      />
    );
  };

  /*
   * The installer, offered from every page and from neither bar's link groups.
   *
   * An outline button rather than the accent one: the accent in this bar is
   * spent on `Register`, and two filled buttons a row apart would be two
   * primary actions asking for the same click. Section 04 — the accent means
   * *this one*, and it can only mean that once.
   *
   * **Hidden when the launcher is what is asking.** `standing.launcher` is the
   * one field in a standing that is about the program rather than the account
   * (see `NavStanding.launcher`), and it is exactly the question here: offering
   * somebody the installer for the application they are already inside is noise
   * on every screen of the desktop client.
   *
   * `null` and not `false` when it is hidden, so the two shapes below can drop
   * it into a row without either of them having to restate the condition.
   */
  const installer = standing?.launcher ? null : (
    <Link href="/download" className={styles.navLauncher}>
      Install launcher
    </Link>
  );

  /*
   * The account cluster, for a surface that has no widget to put in it.
   *
   * The React app always passes `account`, because it knows the name to render
   * and where signing out lives. The storefront could not: it was Astro, and
   * markup written in an `.astro` file is not a React node — passing one is the
   * failure that answered every page on the site with a 200 and an empty body.
   *
   * So the fallback answers both states rather than only one. It used to be
   * `Sign in` unconditionally, which was right while the store could not tell
   * the difference and became the visible half of the bug once it could: a
   * signed-in player was still being offered the door they had already come
   * through.
   *
   * **And it says the name now** (#113). It said the literal word "Account"
   * while the React app's own bar said who you were, which is the one entry in
   * this bar a person reads to check the machine they are on is signed in as
   * them — and on a shared machine it is the entry that matters most. A string
   * crosses the gap a node cannot; see `accountName` above.
   *
   * `displayName` decides what counts as one, here as well as at both ends of
   * the cookie, so an account whose name is the empty string cannot render a
   * link with no label — a target nobody can see and nobody can click, which is
   * exactly what the React app shipped until #113 normalised the same value.
   *
   * Both hrefs go through `href`, so they stay relative to whatever host
   * answered.
   */
  const doors =
    account ??
    (signedIn ? (
      <Link href="/account">{displayName(props.accountName) ?? 'Account'}</Link>
    ) : (
      <>
        <Link href="/sign-in">Sign in</Link>
        {/*
          The other door, and the one this bar never drew.

          **A button rather than a fifth link, because it is not a place — it is
          the decision the whole shop is asking for.** `Sign in` and `Register`
          read as one pair and answer two completely different questions: one is
          for somebody who has been here, the other is the only entry a
          first-time visitor has. Set as identical links they were two words a
          person had to stop and tell apart, and the one that matters more was
          the second of them.

          So the accent is spent here and nowhere else in the header — section
          04's rule that the accent means *this one*. `Sign in` keeps the plain
          link it has always been, which is what makes the contrast do the work:
          a returning player finds their door because it is the quiet one.

          Signed out only. It sits in the same fallback as `Sign in`, so an
          account that is already in has neither offered to it.
        */}
        <Link href="/sign-up" className={styles.navRegister}>
          Register
        </Link>
      </>
    ));

  /*
   * The bar itself, rendered here rather than by whatever is above it.
   *
   * `Page` used to supply `.navBar` and the container inside it, which meant
   * the React app had a header and the storefront — which rendered this
   * component directly — had a row of links flush against the window edges,
   * with none of the bar's ground, border or gutter. A component that looks
   * different depending on who remembered to wrap it is not a shared component.
   *
   * The chrome around the rows is identical in both shapes, so it is written
   * once and the shape is a child of it. Written as two returns, the accent
   * rule, the scroll progress, the measure and the gutter would each have two
   * definitions that have to stay the same — and the signed-out bar is the one
   * a visitor sees first.
   */
  return (
    <div className={styles.navBar}>
      {/*
        How far down the page the reader is, drawn along the bar's own bottom
        edge. Decorative to a screen reader — it carries nothing a person cannot
        get from the scrollbar and from the document itself — and it renders as
        the plain accent rule the bar has always had wherever scroll timelines
        are missing or the page is too short to scroll. See `.navProgress`.
      */}
      <i className={styles.navProgress} aria-hidden="true" />

      <div className={styles.navBarInner}>
        {signedIn ? (
          /*
            Signed in: two rows, because there is a hierarchy to draw.

            **The split is by kind rather than by fit.** The bar held the mark,
            the destinations and the account cluster on a single line, which
            made all three the same weight — the wordmark competing with `Games`
            competing with `Sign in`, none of them the thing the eye lands on.
            The mark is not a destination, the launcher is not a page, and a
            a preference panel is not somewhere the product is; both belong above the
            line, small, where they say what this is and stay out of the way.
            Everywhere you can actually go is the row below, at a size that
            makes the choice the loudest thing in the header.
          */
          <nav className={styles.nav} aria-label="Primary">
            {/*
              The identity row: who this is, and everything a person can reach
              that is not a place to go.

              **`utility` is what changed here.** This row was the mark and the
              installer, and the wayfinding row below it was entries of two
              different kinds — `Games` and `Library` naming the product, and
              `Launcher` and `Admin` naming tools, all at 2.25rem. The tools are
              furniture, and furniture goes where the furniture is. See
              `NavItem.slot`, which is where that argument lives.
            */}
            <div className={styles.navTop}>
              {/*
                The horizontal lockup: guidelines section 02, for navigation
                bars and anywhere without vertical room for the stacked mark.

                1.125rem rather than 1.35: section 07's application mockups set
                the whole bar at one optical weight, and a mark half again the
                size of the labels beside it made the header a headline with
                footnotes next to it.
              */}
              <Logo horizontal size="1.125rem" site={site} />

              {/*
                The furniture, in one group at the far end of the row.

                Grouped rather than spread, so the row stays two objects — the
                mark, and the things you reach for — instead of a mark and a
                scatter of four unrelated controls. `.navUtility` holds the
                installer as well, which is why the stylesheet reaches the links
                in it as `a:not(.navLauncher)`: the button is a control and must
                not grow the destinations' sweeping accent underline.
              */}
              <div className={styles.navUtility}>
                {utility.map(entry)}
                <SoundToggle />
                {installer}
              </div>
            </div>

            {/*
              The wayfinding row: everywhere this account can go, and who they
              are.

              Set several times the size of the row above it, which is the whole
              reason there are two rows. A header's job is to answer "where can
              I go" before anything else on the page, and it was answering it in
              eleven-pixel mono beside a wordmark the same size.
            */}
            <div className={styles.navMain}>
              {/*
                Every entry resolved, without exception. They went out raw once,
                which was survivable only while the store was the only surface
                rendering them: most of these are the store's own paths and the
                app draws them too, so an unresolved `/browse` was the app's
                not-found page.
              */}
              <div className={styles.navPrimary}>{primary.map(entry)}</div>

              <div className={styles.navAccount}>{doors}</div>
            </div>
          </nav>
        ) : (
          /*
            Signed out: one row, because there is nothing to rank.

            `topNav` returns a single entry to a visitor — the catalogue — and
            the account cluster is the two doors. Stacked, that produced a
            second row holding one word, under a hairline whose only job was to
            separate a wordmark from the link beneath it: a hierarchy drawn
            between a thing and nothing.

            So the four objects sit on one line at one optical weight — which is
            what the bar was before there was enough in it to need two rows, and
            what it should go back to whenever there is not. `.navSingle` turns
            the column into a row and brings `--gb-nav-major` down to the
            wordmark's size, so `GAMES` does not become a headline with the mark
            as its footnote — the same mistake the two-row bar was fixing, made
            in the other direction.

            The groups inside are the ones the signed-in bar uses, so the
            current-page marker, the hover sweep and the account fallback are
            one implementation rather than a second one that looks like it.
          */
          <nav className={`${styles.nav} ${styles.navSingle}`} aria-label="Primary">
            <div className={styles.navSingleStart}>
              <Logo horizontal size="1.125rem" site={site} />
              <div className={styles.navPrimary}>{primary.map(entry)}</div>
            </div>

            {/*
              The actions, at the other end: get the program, or come in.

              The installer first and the doors after it, in the order a visitor
              decides them — the shop is readable without an account (section
              29), so the thing that does not ask who you are goes first, and
              the accent on `Register` is still the last thing in the row.
            */}
            <div className={styles.navSingleEnd}>
              <SoundToggle />
              {installer}
              <div className={styles.navAccount}>{doors}</div>
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
